import { useState, useRef } from "react";
import {
  ChevronRight,
  ChevronLeft,
  DollarSign,
  Bug,
  GitBranch,
  Clock,
  Shield,
  HelpCircle,
  X,
  ImagePlus,
  FileIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DisputeCategory } from "@shared/types/DisputeDto";
import { useDisputes } from "@/providers/DisputeProvider";

const MAX_FILES = 4;
const MAX_FILE_SIZE = 500 * 1024;
const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
];
const ALLOWED_EXTENSIONS = ".png,.jpg,.jpeg,.pdf";

interface DisputeIntakeFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CategoryConfig {
  icon: React.ReactNode;
  label: string;
  description: string;
  color: string;
}

const CATEGORY_CONFIG: Record<DisputeCategory, CategoryConfig> = {
  BILLING: {
    icon: <DollarSign className="h-5 w-5" />,
    label: "Billing",
    description: "Invoice amounts, payment discrepancies, or overcharges",
    color: "text-emerald-400",
  },
  QUALITY: {
    icon: <Bug className="h-5 w-5" />,
    label: "Quality",
    description: "Deliverable quality, bugs, or unmet specifications",
    color: "text-red-400",
  },
  SCOPE_CREEP: {
    icon: <GitBranch className="h-5 w-5" />,
    label: "Scope Creep",
    description: "Unauthorized scope changes or feature additions",
    color: "text-orange-400",
  },
  TIMELINE: {
    icon: <Clock className="h-5 w-5" />,
    label: "Timeline",
    description: "Missed deadlines or delivery delays",
    color: "text-blue-400",
  },
  IP_RIGHTS: {
    icon: <Shield className="h-5 w-5" />,
    label: "IP Rights",
    description: "Code ownership, licensing, or asset rights",
    color: "text-violet-400",
  },
  OTHER: {
    icon: <HelpCircle className="h-5 w-5" />,
    label: "Other",
    description: "Issues not covered by other categories",
    color: "text-gray-400",
  },
};

interface BillingFields {
  invoiceNumber: string;
  billedAmount: string;
  expectedAmount: string;
  lineItems: string;
  discrepancyDetails: string;
}

interface QualityFields {
  affectedDeliverable: string;
  expectedBehavior: string;
  actualBehavior: string;
  impactSeverity: string;
  stepsToReproduce: string;
}

interface ScopeCreepFields {
  originalScopeRef: string;
  addedItems: string;
  agreedDeliverables: string;
  budgetImpact: string;
  timelineImpact: string;
}

interface TimelineFields {
  originalDeadline: string;
  actualDeliveryDate: string;
  milestonesAffected: string;
  businessImpact: string;
  communicationHistory: string;
}

interface IpRightsFields {
  ipConcernType: string;
  specificAssets: string;
  agreementReference: string;
  concernDescription: string;
}

interface OtherFields {
  subject: string;
  detailedDescription: string;
}

type CategoryFields =
  | BillingFields
  | QualityFields
  | ScopeCreepFields
  | TimelineFields
  | IpRightsFields
  | OtherFields;

function getInitialFields(category: DisputeCategory): CategoryFields {
  switch (category) {
    case "BILLING":
      return {
        invoiceNumber: "",
        billedAmount: "",
        expectedAmount: "",
        lineItems: "",
        discrepancyDetails: "",
      };
    case "QUALITY":
      return {
        affectedDeliverable: "",
        expectedBehavior: "",
        actualBehavior: "",
        impactSeverity: "",
        stepsToReproduce: "",
      };
    case "SCOPE_CREEP":
      return {
        originalScopeRef: "",
        addedItems: "",
        agreedDeliverables: "",
        budgetImpact: "",
        timelineImpact: "",
      };
    case "TIMELINE":
      return {
        originalDeadline: "",
        actualDeliveryDate: "",
        milestonesAffected: "",
        businessImpact: "",
        communicationHistory: "",
      };
    case "IP_RIGHTS":
      return {
        ipConcernType: "",
        specificAssets: "",
        agreementReference: "",
        concernDescription: "",
      };
    case "OTHER":
      return { subject: "", detailedDescription: "" };
  }
}

function generateDescription(
  category: DisputeCategory,
  fields: CategoryFields
): string {
  switch (category) {
    case "BILLING": {
      const f = fields as BillingFields;
      const parts = [`Invoice: ${f.invoiceNumber}`];
      if (f.billedAmount)
        parts.push(
          `Billed: $${f.billedAmount} | Expected: $${f.expectedAmount}`
        );
      if (f.lineItems) parts.push(`Line items in question: ${f.lineItems}`);
      if (f.discrepancyDetails) parts.push(f.discrepancyDetails);
      return parts.join("\n\n");
    }
    case "QUALITY": {
      const f = fields as QualityFields;
      const parts = [`Affected deliverable: ${f.affectedDeliverable}`];
      if (f.impactSeverity) parts.push(`Severity: ${f.impactSeverity}`);
      if (f.expectedBehavior)
        parts.push(`Expected behavior: ${f.expectedBehavior}`);
      if (f.actualBehavior) parts.push(`Actual behavior: ${f.actualBehavior}`);
      if (f.stepsToReproduce)
        parts.push(`Steps to reproduce: ${f.stepsToReproduce}`);
      return parts.join("\n\n");
    }
    case "SCOPE_CREEP": {
      const f = fields as ScopeCreepFields;
      const parts = [];
      if (f.originalScopeRef)
        parts.push(`Original scope reference: ${f.originalScopeRef}`);
      if (f.agreedDeliverables)
        parts.push(`Agreed deliverables: ${f.agreedDeliverables}`);
      if (f.addedItems) parts.push(`Items added outside scope: ${f.addedItems}`);
      if (f.budgetImpact) parts.push(`Budget impact: ${f.budgetImpact}`);
      if (f.timelineImpact) parts.push(`Timeline impact: ${f.timelineImpact}`);
      return parts.join("\n\n");
    }
    case "TIMELINE": {
      const f = fields as TimelineFields;
      const parts = [];
      if (f.originalDeadline)
        parts.push(`Original deadline: ${f.originalDeadline}`);
      if (f.actualDeliveryDate)
        parts.push(`Actual/expected delivery: ${f.actualDeliveryDate}`);
      if (f.milestonesAffected)
        parts.push(`Milestones affected: ${f.milestonesAffected}`);
      if (f.businessImpact)
        parts.push(`Business impact: ${f.businessImpact}`);
      if (f.communicationHistory)
        parts.push(`Communication history: ${f.communicationHistory}`);
      return parts.join("\n\n");
    }
    case "IP_RIGHTS": {
      const f = fields as IpRightsFields;
      const parts = [];
      if (f.ipConcernType) parts.push(`Type of concern: ${f.ipConcernType}`);
      if (f.specificAssets)
        parts.push(`Assets in question: ${f.specificAssets}`);
      if (f.agreementReference)
        parts.push(`Agreement/license reference: ${f.agreementReference}`);
      if (f.concernDescription) parts.push(f.concernDescription);
      return parts.join("\n\n");
    }
    case "OTHER": {
      const f = fields as OtherFields;
      const parts = [];
      if (f.subject) parts.push(`Subject: ${f.subject}`);
      if (f.detailedDescription) parts.push(f.detailedDescription);
      return parts.join("\n\n");
    }
  }
}

function isCategoryFieldsValid(
  category: DisputeCategory,
  fields: CategoryFields
): boolean {
  switch (category) {
    case "BILLING": {
      const f = fields as BillingFields;
      return !!(f.invoiceNumber && f.billedAmount && f.discrepancyDetails);
    }
    case "QUALITY": {
      const f = fields as QualityFields;
      return !!(f.affectedDeliverable && f.expectedBehavior && f.actualBehavior);
    }
    case "SCOPE_CREEP": {
      const f = fields as ScopeCreepFields;
      return !!(f.addedItems && f.agreedDeliverables);
    }
    case "TIMELINE": {
      const f = fields as TimelineFields;
      return !!(f.originalDeadline && f.businessImpact);
    }
    case "IP_RIGHTS": {
      const f = fields as IpRightsFields;
      return !!(f.ipConcernType && f.specificAssets && f.concernDescription);
    }
    case "OTHER": {
      const f = fields as OtherFields;
      return !!(f.subject && f.detailedDescription);
    }
  }
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

function BillingForm({
  fields,
  onChange,
}: {
  fields: BillingFields;
  onChange: (f: BillingFields) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Invoice Number *</Label>
          <Input
            value={fields.invoiceNumber}
            onChange={(e) =>
              onChange({ ...fields, invoiceNumber: e.target.value })
            }
            placeholder="e.g. INV-2026-042"
            className="glass-input mt-1.5"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Billed Amount *</Label>
            <div className="relative mt-1.5">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={fields.billedAmount}
                onChange={(e) =>
                  onChange({ ...fields, billedAmount: e.target.value })
                }
                placeholder="0.00"
                type="number"
                step="0.01"
                className="glass-input pl-9"
              />
            </div>
          </div>
          <div>
            <Label>Expected Amount</Label>
            <div className="relative mt-1.5">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={fields.expectedAmount}
                onChange={(e) =>
                  onChange({ ...fields, expectedAmount: e.target.value })
                }
                placeholder="0.00"
                type="number"
                step="0.01"
                className="glass-input pl-9"
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        <Label>Line Items in Question</Label>
        <Textarea
          value={fields.lineItems}
          onChange={(e) => onChange({ ...fields, lineItems: e.target.value })}
          placeholder="List the specific line items or charges you're disputing..."
          rows={3}
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Discrepancy Details *</Label>
        <Textarea
          value={fields.discrepancyDetails}
          onChange={(e) =>
            onChange({ ...fields, discrepancyDetails: e.target.value })
          }
          placeholder="Explain what is incorrect about the billing..."
          rows={4}
          className="glass-input mt-1.5"
        />
      </div>
    </div>
  );
}

function QualityForm({
  fields,
  onChange,
}: {
  fields: QualityFields;
  onChange: (f: QualityFields) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Affected Deliverable / Milestone *</Label>
          <Input
            value={fields.affectedDeliverable}
            onChange={(e) =>
              onChange({ ...fields, affectedDeliverable: e.target.value })
            }
            placeholder="e.g. Payment Integration Module"
            className="glass-input mt-1.5"
          />
        </div>
        <div>
          <Label>Impact Severity</Label>
          <Select
            value={fields.impactSeverity}
            onValueChange={(v) => onChange({ ...fields, impactSeverity: v })}
          >
            <SelectTrigger className="glass-input mt-1.5">
              <SelectValue placeholder="Select severity..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Minor">Minor - Cosmetic or low impact</SelectItem>
              <SelectItem value="Moderate">Moderate - Functional but degraded</SelectItem>
              <SelectItem value="Major">Major - Core functionality broken</SelectItem>
              <SelectItem value="Critical">Critical - System unusable</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Expected Behavior *</Label>
        <Textarea
          value={fields.expectedBehavior}
          onChange={(e) =>
            onChange({ ...fields, expectedBehavior: e.target.value })
          }
          placeholder="Describe what the deliverable should do according to the specification..."
          rows={3}
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Actual Behavior *</Label>
        <Textarea
          value={fields.actualBehavior}
          onChange={(e) =>
            onChange({ ...fields, actualBehavior: e.target.value })
          }
          placeholder="Describe what the deliverable actually does..."
          rows={3}
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Steps to Reproduce</Label>
        <Textarea
          value={fields.stepsToReproduce}
          onChange={(e) =>
            onChange({ ...fields, stepsToReproduce: e.target.value })
          }
          placeholder="1. Navigate to...&#10;2. Click on...&#10;3. Observe..."
          rows={3}
          className="glass-input mt-1.5"
        />
      </div>
    </div>
  );
}

function ScopeCreepForm({
  fields,
  onChange,
}: {
  fields: ScopeCreepFields;
  onChange: (f: ScopeCreepFields) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Original Scope / SOW Reference</Label>
        <Input
          value={fields.originalScopeRef}
          onChange={(e) =>
            onChange({ ...fields, originalScopeRef: e.target.value })
          }
          placeholder="e.g. SOW Section 3.2, Sprint 4 backlog"
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Agreed Deliverables *</Label>
        <Textarea
          value={fields.agreedDeliverables}
          onChange={(e) =>
            onChange({ ...fields, agreedDeliverables: e.target.value })
          }
          placeholder="List the deliverables that were originally agreed upon..."
          rows={3}
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Items Added Outside Scope *</Label>
        <Textarea
          value={fields.addedItems}
          onChange={(e) =>
            onChange({ ...fields, addedItems: e.target.value })
          }
          placeholder="List features or work items that were added beyond the original scope..."
          rows={3}
          className="glass-input mt-1.5"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Budget Impact</Label>
          <Input
            value={fields.budgetImpact}
            onChange={(e) =>
              onChange({ ...fields, budgetImpact: e.target.value })
            }
            placeholder="e.g. $5,000 additional cost"
            className="glass-input mt-1.5"
          />
        </div>
        <div>
          <Label>Timeline Impact</Label>
          <Input
            value={fields.timelineImpact}
            onChange={(e) =>
              onChange({ ...fields, timelineImpact: e.target.value })
            }
            placeholder="e.g. 2 week delay"
            className="glass-input mt-1.5"
          />
        </div>
      </div>
    </div>
  );
}

function TimelineForm({
  fields,
  onChange,
}: {
  fields: TimelineFields;
  onChange: (f: TimelineFields) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Original Deadline *</Label>
          <Input
            type="date"
            value={fields.originalDeadline}
            onChange={(e) =>
              onChange({ ...fields, originalDeadline: e.target.value })
            }
            className="glass-input mt-1.5"
          />
        </div>
        <div>
          <Label>Actual / Expected Delivery Date</Label>
          <Input
            type="date"
            value={fields.actualDeliveryDate}
            onChange={(e) =>
              onChange({ ...fields, actualDeliveryDate: e.target.value })
            }
            className="glass-input mt-1.5"
          />
        </div>
      </div>
      <div>
        <Label>Milestones Affected</Label>
        <Textarea
          value={fields.milestonesAffected}
          onChange={(e) =>
            onChange({ ...fields, milestonesAffected: e.target.value })
          }
          placeholder="Which milestones or deliverables were delayed..."
          rows={3}
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Business Impact *</Label>
        <Textarea
          value={fields.businessImpact}
          onChange={(e) =>
            onChange({ ...fields, businessImpact: e.target.value })
          }
          placeholder="How has the delay affected your business operations, launch timeline, revenue, etc..."
          rows={4}
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Communication History</Label>
        <Textarea
          value={fields.communicationHistory}
          onChange={(e) =>
            onChange({ ...fields, communicationHistory: e.target.value })
          }
          placeholder="Were deadline changes communicated in advance? Summarize relevant communications..."
          rows={3}
          className="glass-input mt-1.5"
        />
      </div>
    </div>
  );
}

function IpRightsForm({
  fields,
  onChange,
}: {
  fields: IpRightsFields;
  onChange: (f: IpRightsFields) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Type of IP Concern *</Label>
        <Select
          value={fields.ipConcernType}
          onValueChange={(v) => onChange({ ...fields, ipConcernType: v })}
        >
          <SelectTrigger className="glass-input mt-1.5">
            <SelectValue placeholder="Select concern type..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Code Ownership">Code Ownership</SelectItem>
            <SelectItem value="Design Assets">Design Asset Rights</SelectItem>
            <SelectItem value="Licensing">Licensing / Usage Rights</SelectItem>
            <SelectItem value="Trade Secrets">Trade Secrets / Confidentiality</SelectItem>
            <SelectItem value="Third-Party IP">Third-Party IP Usage</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Specific Assets in Question *</Label>
        <Textarea
          value={fields.specificAssets}
          onChange={(e) =>
            onChange({ ...fields, specificAssets: e.target.value })
          }
          placeholder="List the specific code, designs, or assets that are in dispute..."
          rows={3}
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Agreement / License Reference</Label>
        <Input
          value={fields.agreementReference}
          onChange={(e) =>
            onChange({ ...fields, agreementReference: e.target.value })
          }
          placeholder="e.g. MSA Section 8, NDA Clause 4.2"
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Concern Description *</Label>
        <Textarea
          value={fields.concernDescription}
          onChange={(e) =>
            onChange({ ...fields, concernDescription: e.target.value })
          }
          placeholder="Describe your IP concern in detail..."
          rows={4}
          className="glass-input mt-1.5"
        />
      </div>
    </div>
  );
}

function OtherForm({
  fields,
  onChange,
}: {
  fields: OtherFields;
  onChange: (f: OtherFields) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Subject *</Label>
        <Input
          value={fields.subject}
          onChange={(e) => onChange({ ...fields, subject: e.target.value })}
          placeholder="Brief summary of the issue"
          className="glass-input mt-1.5"
        />
      </div>
      <div>
        <Label>Detailed Description *</Label>
        <Textarea
          value={fields.detailedDescription}
          onChange={(e) =>
            onChange({ ...fields, detailedDescription: e.target.value })
          }
          placeholder="Provide a thorough description of the issue, including any relevant context, dates, and impact..."
          rows={8}
          className="glass-input mt-1.5"
        />
      </div>
    </div>
  );
}

function CategorySpecificForm({
  category,
  fields,
  onChange,
}: {
  category: DisputeCategory;
  fields: CategoryFields;
  onChange: (f: CategoryFields) => void;
}) {
  switch (category) {
    case "BILLING":
      return (
        <BillingForm
          fields={fields as BillingFields}
          onChange={onChange}
        />
      );
    case "QUALITY":
      return (
        <QualityForm
          fields={fields as QualityFields}
          onChange={onChange}
        />
      );
    case "SCOPE_CREEP":
      return (
        <ScopeCreepForm
          fields={fields as ScopeCreepFields}
          onChange={onChange}
        />
      );
    case "TIMELINE":
      return (
        <TimelineForm
          fields={fields as TimelineFields}
          onChange={onChange}
        />
      );
    case "IP_RIGHTS":
      return (
        <IpRightsForm
          fields={fields as IpRightsFields}
          onChange={onChange}
        />
      );
    case "OTHER":
      return (
        <OtherForm
          fields={fields as OtherFields}
          onChange={onChange}
        />
      );
  }
}

export function DisputeIntakeForm({
  open,
  onOpenChange,
}: DisputeIntakeFormProps) {
  const { submitDispute } = useDisputes();
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState<DisputeCategory | "">("");
  const [categoryFields, setCategoryFields] = useState<CategoryFields | null>(
    null
  );
  const [requestedOutcome, setRequestedOutcome] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setStep(1);
    setCategory("");
    setCategoryFields(null);
    setRequestedOutcome("");
    filePreviews.forEach((url) => URL.revokeObjectURL(url));
    setFiles([]);
    setFilePreviews([]);
    setIsSubmitting(false);
  };

  const handleCategorySelect = (cat: DisputeCategory) => {
    setCategory(cat);
    setCategoryFields(getInitialFields(cat));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    const previews: string[] = [];

    for (const file of incoming) {
      if (files.length + validFiles.length >= MAX_FILES) break;
      if (!ALLOWED_TYPES.includes(file.type)) continue;
      if (file.size > MAX_FILE_SIZE) continue;
      validFiles.push(file);
      if (file.type.startsWith("image/")) {
        previews.push(URL.createObjectURL(file));
      } else {
        previews.push("");
      }
    }

    setFiles((prev) => [...prev, ...validFiles]);
    setFilePreviews((prev) => [...prev, ...previews]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    if (filePreviews[index]) URL.revokeObjectURL(filePreviews[index]);
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!category || !categoryFields) return;

    const description = generateDescription(
      category as DisputeCategory,
      categoryFields
    );

    setIsSubmitting(true);
    try {
      await submitDispute({
        category: category as DisputeCategory,
        description,
        requestedOutcome,
        files: files.length > 0 ? files : undefined,
      });
      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to submit dispute:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) resetForm();
    onOpenChange(open);
  };

  const categories: DisputeCategory[] = [
    "BILLING",
    "QUALITY",
    "SCOPE_CREEP",
    "TIMELINE",
    "IP_RIGHTS",
    "OTHER",
  ];

  const canProceedStep2 =
    category && categoryFields
      ? isCategoryFieldsValid(category as DisputeCategory, categoryFields)
      : false;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto glass-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {step > 1 && category && (
              <span
                className={`${CATEGORY_CONFIG[category as DisputeCategory].color}`}
              >
                {CATEGORY_CONFIG[category as DisputeCategory].icon}
              </span>
            )}
            <span>
              {step === 1 && "Select Dispute Category"}
              {step === 2 &&
                category &&
                `${CATEGORY_CONFIG[category as DisputeCategory].label} Dispute Details`}
              {step === 3 && "Evidence & Outcome"}
            </span>
          </DialogTitle>
          <div className="flex gap-1 mt-3">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  s <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Select the category that best describes your dispute
            </p>
            <div className="grid grid-cols-2 gap-3">
              {categories.map((cat) => {
                const config = CATEGORY_CONFIG[cat];
                return (
                  <button
                    key={cat}
                    onClick={() => handleCategorySelect(cat)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      category === cat
                        ? "border-primary bg-primary/10"
                        : "border-border/50 hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-1">
                      <span className={config.color}>{config.icon}</span>
                      <span className="font-medium">{config.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-8">
                      {config.description}
                    </p>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => setStep(2)}
                disabled={!category}
                className="gradient-primary"
              >
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && category && categoryFields && (
          <div className="space-y-4 mt-2">
            <div className="bg-muted/20 rounded-lg p-3 border border-border/30">
              <p className="text-xs text-muted-foreground">
                Fields marked with * are required. This information will be used
                to generate the dispute description.
              </p>
            </div>

            <CategorySpecificForm
              category={category as DisputeCategory}
              fields={categoryFields}
              onChange={setCategoryFields}
            />

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!canProceedStep2}
                className="gradient-primary"
              >
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5 mt-2">
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Upload Evidence
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                PNG, JPG, or PDF. Max {MAX_FILES} files, 500KB each.
              </p>

              {files.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="relative group flex items-center gap-3 p-2.5 border border-border/50 rounded-lg bg-muted/10"
                    >
                      {filePreviews[index] ? (
                        <img
                          src={filePreviews[index]}
                          alt={file.name}
                          className="w-12 h-12 object-cover rounded-md border border-border/50"
                        />
                      ) : (
                        <div className="w-12 h-12 flex items-center justify-center rounded-md border border-border/50 bg-muted/20">
                          <FileIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">
                          {file.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {files.length < MAX_FILES && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-border/50 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_EXTENSIONS}
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <ImagePlus className="h-7 w-7 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Click to select files or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {files.length}/{MAX_FILES} files attached
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">
                Requested Outcome *
              </Label>
              <Textarea
                value={requestedOutcome}
                onChange={(e) => setRequestedOutcome(e.target.value)}
                placeholder="What resolution are you seeking? (e.g., Credit, Rework, Cancellation, Revised timeline)"
                rows={4}
                className="glass-input"
              />
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!requestedOutcome.trim() || isSubmitting}
                className="gradient-primary"
              >
                {isSubmitting ? "Submitting..." : "Submit Dispute"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
